import React, { useState, useEffect, useRef } from 'react';
import { getMarkDown, useDetectMobile, useNextButtonWidthClass, formatCardLoadTiming, getButtonWithArrow } from '../utils';
import TextField from '@mui/material/TextField';
import Footer from './Footer';
import { useTiming } from '../TimingContext';
import { useCardLoadTiming } from '../hooks/useCardLoadTiming';
import '../../../stylesheets/triagebot/AgeCard.scss';
import InputAdornment from '@mui/material/InputAdornment';

const AgeCardMessage = ({ message, sendMessage, disabled }) => {
    const [age, setAge] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [editing, setEditing] = useState(false);
    const [valid, setValid] = useState(false);

    const questionId = (message && message.data && message.data.length && message.data[0] && message.data[0].question_id)
        ? message.data[0].question_id
        : -1;

    const isDisabled = submitted || disabled;
    const cdoname = message?.data?.[0]?.cdoName;    
    const { isMobile } = useDetectMobile();
    const nextButtonClass = useNextButtonWidthClass();
    const subHeaderText = (message?.data?.[0]?.subHeaderText)
    ? message.data[0].subHeaderText
    : '';
    const nextButton =(message.data?.[0].nextButtonText)
        ? message.data?.[0].nextButtonText
        : ''
    const agePromptText = (message?.data?.[0]?.ageText)
    ? message.data[0].ageText
    : '';
    const ageInYearsText = (message?.data?.[0]?.ageInYears)
    ? message.data[0].ageInYears
    : '';
    const hasSubHeader = Boolean(subHeaderText);
    const subHeaderId = hasSubHeader ? `${questionId || 'age-card'}-subheader` : undefined;

    const cardName = questionId !== -1 ? questionId : "AgeCard";
    const { logTiming } = useTiming();

    useCardLoadTiming({
        sentAt: message._sentAt,
        cardName,
        logTiming,
        formatCardLoadTiming
    });

   const ageChanged = (e) => {
        const age = parseInt(e.target.value.replace(/[^0-9]/g, ''));

        if (isNaN(age)) {
            setValid(false);
            setAge('');
            return;
        }

        setAge(age);
        setValid(cdoname=='Open' ? (age >= 18 && age <= 120) : (age >= 1 && age <= 120));
        
    }

    const cardRef = useRef(null);
    const inputRef = useRef(null);
    const headerRef = useRef(null);

    const stepAge = (direction) => {
        const input = inputRef.current?.querySelector('input');
        if (!input) return;
        direction === 'up' ? input.stepUp() : input.stepDown();
        ageChanged({ target: input });
    };

    const sendAgeMessage = () => {
        if (submitted) {
            sendMessage('rewind', questionId, questionId);
            setSubmitted(false);
            setEditing(true);
            return;
        }

        setSubmitted(true);

        editing
            ? sendMessage('message', `##edit@${message.data[0].question_id}@${age || 'none'}`, `##edit@${message.data[0].question_id}@${age || 'none'}`)
            : sendMessage('message', age, age)
    }
    
    useEffect(() => {
        if (!isDisabled) return;

        if (cardRef.current && cardRef.current.contains(document.activeElement) && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, [isDisabled]);

    useEffect(() => {
        if (isDisabled) return;

        const focusTimer = setTimeout(() => {
            headerRef.current?.focus({ preventScroll: true });
        }, 500);

        return () => clearTimeout(focusTimer);
    }, [cardName, isDisabled]);

    return (
        <div ref={cardRef} className={isDisabled ? 'disabled age card' : 'age card'}>
            <div className='content'>
                <div className='header'>
                    <h2 id='age-card-label' ref={headerRef} className='headerText' tabIndex={-1}>{getMarkDown(message)}</h2>
                     {
                                            hasSubHeader &&
                                            <div id={subHeaderId} className='subheader'>
                        {getMarkDown(subHeaderText)}
                      </div>
                     }
                </div>
                <div className='body'>
                    <TextField
                        ref={inputRef}
                        autoFocus={false}
                        autoComplete='off'
                        type='number'
                        placeholder={agePromptText}
                        className={(isMobile ? 'mobile ' : '') + 'age-input'}
                        variant="outlined"
                        value={age}
                        onChange={ageChanged}
                        inputProps={{ maxLength: 3, inputMode: 'numeric', 'aria-label': 'Enter your age', 'aria-describedby': subHeaderId, autoComplete: 'off' }}
                        disabled={isDisabled}
                        InputProps={{
                        endAdornment: (
                        <InputAdornment position="end">
                            <div className='age-spinner-buttons'>
                                <button type='button' className='age-spinner-btn' onClick={() => stepAge('up')} disabled={isDisabled} tabIndex={-1} aria-label='Increase age'>&#9650;</button>
                                <button type='button' className='age-spinner-btn' onClick={() => stepAge('down')} disabled={isDisabled} tabIndex={-1} aria-label='Decrease age'>&#9660;</button>
                            </div>
                            <span className='labeltext'>{ageInYearsText}</span>
                        </InputAdornment>
                        )
                        }}
                    />
                </div>
            </div>
            <Footer
                showChangeButton={submitted && !message.isLastMessage}
                onAccepted={sendAgeMessage}
                onDenied={() => sendMessage('fastforward')}
                disabled={disabled}
                divID={questionId}
                message={message}
            >
                {
                    submitted ||
                    <button
                        disabled={!valid || isDisabled}
                        className={`submit-button ${nextButtonClass}`.trim()}
                        onClick={sendAgeMessage}
                        style={{display: submitted ? 'none' : ''}}
                        aria-hidden={isDisabled ? 'true': 'false'}
                    >
                        {getButtonWithArrow(nextButton)}
                    </button>
                }
            </Footer>
        </div >
    );
};

export default AgeCardMessage;
